<script lang="ts" setup>
/**
 * A wrapper around the Vueform Multiselect component, with some customizations.
 * 
 * The wrapper is not (yet) 100% api compatible with the upstream component,
 * so you can extend the api as you see fit anytime.
 * 
 * @see https://github.com/vueform/multiselect
 */
import type { Component } from 'nuxt/schema';
import Multiselect from '@vueform/multiselect'

defineOptions({
  inheritAttrs: false
})

export interface Props {
  mode?: 'single' | 'multiple' | 'tags'
  options: Array<any> | Function
  required?: boolean
  searchable?: boolean
  valueProp?: string
  label?: string
  placeholder?: string
  disabled?: boolean
  limit?: number
  clearOnSearch?: boolean
  closeOnSelect?: boolean
  clearOnBlur?: boolean
  delay?: number
  filterResults?: boolean
  resolveOnLoad?: boolean
  infinite?: boolean
  loading?: boolean
  id?: string
  canClear?: boolean
  searchFilter?: Function | undefined
  noOptionsText?: string
  noResultsText?: string
  classes?: Object
}

export interface Emits {
  (event: 'search-change', query: string, select$: Component): void
}

const props = withDefaults(defineProps<Props>(), {
  mode: 'single',
  options: () => ([]),
  required: false,
  searchable: true,
  valueProp: 'value',
  label: 'label',
  placeholder: '',
  disabled: false,
  limit: 0,
  clearOnSearch: false,
  closeOnSelect: true,
  clearOnBlur: true,
  delay: 0,
  filterResults: true,
  resolveOnLoad: true,
  infinite: false,
  loading: false,
  id: 'multiselect',
  canClear: true,
  searchFilter: undefined,
  noOptionsText: 'The list is empty',
  noResultsText: 'No results found',
  classes: () => ({}),
})

const [modelValue] = defineModel<any>()

const emits = defineEmits<Emits>()

const classes = {
  container: 'multiselect tw-multiselect',
  option: 'multiselect-option tw-multiselect-option',
  ...props.classes
}
</script>

<template>
  <slot name="label" />
  <Multiselect v-bind="$attrs" v-model="modelValue" :mode="mode" :options="options" :required="required"
    :searchable="searchable" :value-prop="valueProp" :label="label" :placeholder="placeholder" :disabled="disabled"
    :limit="limit" :clear-on-search="clearOnSearch" :close-on-select="closeOnSelect" :clear-on-blur="clearOnBlur"
    :delay="delay" :filter-results="filterResults" :resolve-on-load="resolveOnLoad" :infinite="infinite"
    :loading="loading" :id="id" :can-clear="canClear" :search-filter="searchFilter" :no-options-text="noOptionsText"
    :no-results-text="noResultsText" :classes="classes" @search-change="emits('search-change', $event, $el)">
    <template v-if="$slots.placeholder" #placeholder>
      <slot name="placeholder" />
    </template>
    <template v-if="$slots.afterlist" #afterlist>
      <slot name="afterlist" />
    </template>
    <template v-if="$slots.beforelist" #beforelist>
      <slot name="beforelist" />
    </template>
    <template v-if="$slots.multiplelabel" #multiplelabel="{ values }">
      <slot name="multiplelabel" v-bind="{ values }" />
    </template>
    <template v-if="$slots.nooptions" #nooptions>
      <slot name="nooptions" />
    </template>
    <template v-if="$slots.noresults" #noresults>
      <slot name="noresults" />
    </template>
    <template v-if="$slots.grouplabel" #grouplabel="{ group, isPointed, isSelected }">
      <slot name="grouplabel" v-bind="{ group, isPointed, isSelected }" />
    </template>
    <template v-if="$slots.option" #option="{ option, isPointed, isSelected, search }">
      <slot name="option" v-bind="{ option, isPointed, isSelected, search }" />
    </template>
    <template v-if="$slots.singlelabel" #singlelabel="{ value }">
      <slot name="singlelabel" v-bind="{ value }" />
    </template>
    <template v-if="$slots.tag" #tag="{ option, handleTagRemove, disabled }">
      <slot name="tag" v-bind="{ option, handleTagRemove, disabled }" />
    </template>
    <template v-if="$slots.caret" #caret="{ handleCaretClick, isOpen }">
      <slot name="caret" v-bind="{ handleCaretClick, isOpen }" />
    </template>
    <template v-if="$slots.clear" #clear="{ clear }">
      <slot name="clear" v-bind="{ clear }" />
    </template>
    <template v-if="$slots.spinner" #spinner>
      <slot name="spinner" />
    </template>
    <template v-if="$slots.infinite" #infinite>
      <slot name="infinite" />
    </template>
  </Multiselect>
</template>

<style lang="scss">
@import "@vueform/multiselect/themes/default.css";
@import "~/assets/styles/tw-variables.scss";
@import "~/assets/styles/tw-mixins.scss";
@import "~/assets/styles/tw-form.scss";

.tw-multiselect {
  padding: 0.5rem 0;
}

.tw-multiselect,
.tw-multiselect .multiselect-wrapper,
.tw-multiselect .multiselect-search {
  background-color: transparent;
}

.tw-multiselect.is-active {
  border-color: $primary-60;
  box-shadow: 0 0 0 .2rem rgba(250, 148, 0, 0.25);
}

.tw-multiselect-option:hover {
  border-color: $primary;
  background-color: $primary;
  transition: all 0.1s ease-in-out;
}

.tw-multiselect-option.is-selected,
.tw-multiselect-option.is-selected.is-pointed {
  background-color: $primary;
}

.tw-multiselect-option.is-selected:hover,
.tw-multiselect-option.is-selected.is-pointed:hover {
  border-color: $primary-variant-100;
  background-color: $primary-variant-100;
  transition: all 0.1s ease-in-out;
}
</style>